import React, { useState } from 'react'
import { IBanner } from '@/types/banners'
import { useTranslation } from '@/localization/useTranslation'
import { Button } from '@/components/Buttons/Button'
import { Modal } from '@/components/Modal'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import { BannerContent } from '../BannerContent'
import { navigateTo } from '@/utils/url'

import classes from './styles.module.scss'

type NoticeBanner = Extract<IBanner, { type: 'notice' }>

interface INoticeBannerProps {
  dismissBanner: () => void
  bannerProps: NoticeBanner
}

export const NoticeBanner: React.FC<INoticeBannerProps> = (props) => {
  const {
    dismissBanner,
    bannerProps
  } = props
  const { sendGtm } = useTagManager()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { _id, banner, modal } = bannerProps
  const { t } = useTranslation()

  const isClickable = !!modal || !!banner?.action
  const image = banner?.image
  const isBackgroundImage = image?.position === 'background'
  const backgroundStyle = (image && isBackgroundImage)
    ? {
      backgroundImage: `url(${image.src})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
    : undefined

  const handleBannerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (modal) {
      setIsModalVisible(true)
    } else {
      sendGtm.UI_BANNER_CLICKED({
        action: 'click',
        name: 'accept',
        id: _id,
        ...(banner?.action ? { path: banner?.action } : {}),
      })
      // No action configured = informative banner; clicking must not reload the page
      navigateTo(banner?.action)
    }
  }

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const action = banner?.cta?.action ?? banner?.action
    // A CTA without its own destination mirrors the banner click, so a
    // modal-backed banner's CTA opens the modal
    if (!action && modal) {
      setIsModalVisible(true)
      return
    }
    sendGtm.UI_BANNER_CLICKED({
      action: 'click',
      name: 'cta',
      id: _id,
      ...(action ? { path: action } : {}),
    })
    navigateTo(action)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
  }

  const handleModalAction = (e: React.MouseEvent, action: {
    type: string
    url?: string
  }) => {
    e.stopPropagation()
    if (action.type === 'cancel') {
      setIsModalVisible(false)
    } else if (action.type === 'link') {
      sendGtm.UI_BANNER_CLICKED({
        action: 'click',
        name: 'accept',
        id: _id,
        path: action.url || '',
      })
      // Dismiss on a rejected/blocked link so the button is never an inert
      // dead-end that traps the user in the modal (mirrors ConfigurableModal).
      queueMicrotask(() => {
        if (!navigateTo(action.url)) setIsModalVisible(false)
      })
    }
  }

  return (
    <>
      <BannerContent
        text={banner.text}
        image={image}
        cta={banner?.cta}
        isClickable={isClickable}
        dismissible={bannerProps.dismissible}
        tone={bannerProps.tone}
        style={backgroundStyle}
        onClick={handleBannerClick}
        onCtaClick={handleCtaClick}
        onDismiss={dismissBanner}
      />
      {modal && (
        <Modal
          isVisible={isModalVisible}
          onClose={handleModalClose}
        >
          <div className={classes.modal}>
            <h3 className={classes.modal__title}>
              {t(modal?.title)}
            </h3>
            <p className={classes.modal__description}>
              {t(modal?.description)}
            </p>
            <div className={classes.modal__actions}>
              {(modal as { actions?: Array<{ text: any, action: { type: string, variant: 'primary' | 'secondary' | 'tertiary' } }> })?.actions?.map((action, index: number) => (
                <Button
                  key={index}
                  variant={action.action.variant as 'primary' | 'secondary' | 'tertiary'}
                  onClick={(e) => handleModalAction(e, action.action)}
                >
                  {t(action.text)}
                </Button>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
